import { BifrostBackendNestApi, BifrostBackendApi } from "../gateways/BifrostBackendApi";
import { store } from "../store";
import appConfig from "../appconfig";
import ServerLogProvider from "./ServerLogProvider";
import {getExceededPriceRules} from "../libraries/utils";

const tenantConfig = appConfig.tenantConfig;

const getApi = () => {
	const useNestOrderPaymentFlow =
		store.getters.featureToggles &&
		store.getters.featureToggles.useNestOrderPaymentFlow;
	return useNestOrderPaymentFlow ? BifrostBackendNestApi : BifrostBackendApi;
};

const OrdersProvider = {
	findOneUnauthorized(id) {
		return getApi().get(`/orders/${id}`);
	},
	async findOneUnauthorizedNest(id) {
		return (await getApi().get(`/orders/${id}/_byId`)).data;
	},

	find({ queryData, includeCorrespondence = false }) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				...queryData,
				tenantId: tenantConfig.tenantId
			}
		});
	},

	async findOne(
		id,
		includeCorrespondence = false,
		includeIngredients = false,
		deleted = undefined,
		extendedResponse = false,
		includeSnapshots = false
	) {

		return getApi().get(`/orders/${id}`, {
			params: {
				deleted
			},
			headers: {
				...(store.getters.jwtToken ? { Authorization: store.getters.jwtToken } : {}),
				includeCorrespondence,
				includeIngredients,
				extendedResponse,
				includeSnapshots
			}
		});
	},

	findAllPendingDays(includeCorrespondence = false) {
		return getApi().get("/pending-orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			}
		});
	},

	findAllOrdersCreatedBetween(startDate, endDate, includeCorrespondence = false) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				"$limit": "10000",
				"$sort[createdAt]": -1,
				"createdAt[$gte]": startDate,
				"createdAt[$lte]": endDate,
				$select: [
					"orderNumber","tenantId",  "status", "paymentStatus", "pickupDate"
					, "pickupTime", "createdAt", "updatedAt", "pendingAt", "confirmedAt"
				]
			}
		});
	},

	findAllPendingOrders(includeCorrespondence = false) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				tenantId: tenantConfig.tenantId,
				"$limit": "10000",
				"createdBy": "customer",
				"status[$in]": ["pending"],
				$select: ["id", "extId", "productSelections", "changeStatusToken"]
			}
		});
	},

	findAllByIds(ids, includeCorrespondence = false) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				"$limit": "10000",
				"extId[$in]": ids
			}
		});
	},

	findByOrderNumber(ids, includeCorrespondence = false) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				"$limit": "1",
				"orderNumber[$eq]": ids
			}
		});
	},

	findAllWithNewMessages(fromPickupDate) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: true
			},
			params: {
				tenantId: tenantConfig.tenantId,
				"$limit": "10000",
				"newOrderMessageFlag[$eq]": true,
				"shopId[$eq]": store.getters.shopDetails.shopId,
				"deletedAt[$is]": null,
				"status[$in]": ["pending", "confirmed"],
				"pickupDate[$gte]": fromPickupDate,
				$select: ["id", "orderNumber", "shopId", "extId", "newOrderMessageFlag", "pickupDate"]
			}
		});
	},

	findByOrderNumberOrCustomerDetails(searchTerm, includeCorrespondence = false) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params: {
				tenantId: tenantConfig.tenantId,
				["$or[orderNumber][$iLike]"]: "%" + searchTerm + "%",
				["$or[customerDetails.name][$iLike]"]: "%" + searchTerm + "%",
				["$or[customerDetails.email][$iLike]"]: "%" + searchTerm + "%",
				["$or[customerDetails.telephone][$iLike]"]: "%" + searchTerm + "%"
			}
		});
	},

	findAllByParams({
		findParams,
		includeCorrespondence = false,
		includeIngredients = false,
		includeSnapshots = false
	}) {
		return getApi().get("/orders", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence,
				includeIngredients,
				includeSnapshots
			},
			params: {
				...findParams,
				tenantId: tenantConfig.tenantId,
				"$limit": "10000"
			}
		});
	},

	findOrderStatistics(includeCorrespondence = false) {
		return BifrostBackendApi.get("/order-statistics", {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			}
		});
	},

	findOrderPayments(id) {
		return BifrostBackendNestApi.get(`/order-payments/${id}`, {
			headers: {
				Authorization: store.getters.jwtToken
			}
		});
	},

	async findOrderControlPanelData({ startDate, endDate, searchOrders = "", includeCorrespondence = false, tenantId = 0 }) {
		const params = {
			startDate,
			endDate,
			tenantId,
			orderControlPanelData: 1
		};

		if (searchOrders.length) {
			// replace all spaces or new lines with commas

			params["orderNumbersFilter"] = searchOrders.replace(/\s/g, ",");
		}

		const response = await getApi().get(`/order-statistics`, {
			headers: {
				Authorization: store.getters.jwtToken,
				includeCorrespondence: includeCorrespondence
			},
			params
		});

		return response.data;
	},

	refundOrder(orderExtId) {
		return BifrostBackendApi.patch(`/order-refund/${orderExtId}`, {

		}, {
			headers: { Authorization: store.getters.jwtToken }
		}).then(response => {
			return response.data;
		});
	},

	sendOrder({
		shopId, pickupDate, pickupTime, productSelections, customerDetails, orderOptions, channel, integrationType,
		specialNotes, userConsent, isPrepaid = false,
		prepaidUrlAccept = null, prepaidUrlCancel = null, prepaidUrlCheckout = null,
		cartOrderId = null, isEditMode = false, orderChangedDescription = "", orderChangedBy = "",
		isAdmin = false, inPreparation=false,
		subscriptionEnabled = false,
		paymentType = null, redeemedVouchers = null, orderRef = null, extendedData = null,
		persistPaymentDetails = false, recurringPaymentDetails = null
	}) {
		orderOptions.languageCode = store.getters.lastLocale;

		if (store.getters.featureToggles && store.getters.featureToggles.useNestOrderPaymentFlow) {
			orderOptions.useNestOrderPaymentFlow = store.getters.featureToggles.useNestOrderPaymentFlow;
		}

		try {
			// true, 1 - dryRun without Points or Upodi (SunsetPoint/SunsetPLUS)
			// 2 - dryRun with Points
			// 3 - dryRun with Upodi
			// 4 - dryRun with both Points and Upodi
			const validValues = [ true, 1, 2, 3, 4 ];
			const dryRun = JSON.parse(localStorage.getItem("dryRun"));
			if(validValues.includes(dryRun)) {
				orderOptions.dryRun = dryRun;
			}
		} catch(e) {
			// eslint-disable-next-line no-console
			console.log(e);
		}

		const isMobile = appConfig.mobile.isMobileApp();
		const platform = appConfig.mobile.mobileApi().platformId;
		const baseUrl = isMobile ? appConfig.getPaymentUrl() : window.location.origin;
		const prepaidRedirectHelperUrl = `${baseUrl}/redirect-helper`;

		const paymentTypeAssigner = (paymentType)  => paymentType ? { paymentType } : {};
		const subscriptionAssigner = subscriptionEnabled ? { subscriptionEnabled } : {};
		const orderRefAssigner = orderRef ? orderRef : appConfig.clientTraceId();
		const clientTraceId = localStorage.getItem("clientTraceId");
		const orderData = {
			tenantId: tenantConfig.tenantId,
			shopId,
			pickupDate,
			pickupTime,
			productSelections,
			customerDetails: {
				...customerDetails,
				clientTraceId: clientTraceId
			},
			orderOptions,
			specialNotes,
			userConsent,
			isPrepaid,
			prepaidUrlAccept,
			prepaidUrlCancel,
			prepaidUrlCheckout,
			...(platform !== "android" && { prepaidRedirectHelperUrl } ),
			isEditMode,
			orderChangedBy,
			orderChangedDescription,
			isAdmin,
			...subscriptionAssigner,
			inPreparation,
			...paymentTypeAssigner(paymentType),
			redeemedVouchers,
			integrationType,
			channel: channel ? channel : appConfig.channelAlias(),
			orderRef: orderRefAssigner,
			extendedData: {
				...extendedData,
				exceededPriceRules: getExceededPriceRules()
			},
			persistPaymentDetails,
			recurringPaymentDetails
		};
		const headers = {};

		if (isAdmin && store.getters.jwtToken) {
			headers.Authorization = store.getters.jwtToken;
		}
		const customerIdpToken = store.getters.userIdpToken || store.getters.applicationUserIdpToken;
		if (!isAdmin && customerIdpToken) {
			// TODO: this is added for debugging purposes, remove after bug is fixed:
			// https://sunset-boulevard-npd.monday.com/boards/4818172205/pulses/6955960717
			if(orderData.channel === "kiosk" && store.getters.userIdpToken) {
				ServerLogProvider.postEvent({message: "UserIdpToken is " + customerIdpToken, payload: { customerIdpToken, kioskEnv: window.kioskHost, customerDetails, orderRef: orderRefAssigner }});
			}

			headers.Authorizationidp = customerIdpToken;
		}

		if (cartOrderId === null) {
			// new order
			return getApi().post("/orders", orderData, {
				headers: headers
			});
		} else {
			// order already exists in DB, update it
			return getApi().patch(`/orders/${cartOrderId}`, orderData, {
				headers: headers
			}).catch((err) => {
				if (!isEditMode) {
					// if patch is not allowed and this is not edit mode then send regular post with creating new order
					return getApi().post("/orders", orderData, {
						headers: headers
					});
				} else {
					throw err;
				}
			});
		}
	},

	deleteOrder(id) {
		return getApi().delete(`/orders/${id}`, {
			headers: { Authorization: store.getters.jwtToken }
		});
	},

	async changeOrder(id, deleteData){
		const response = await getApi().patch(`/change-order-data/${id}`,
			deleteData,
			{
				headers: {
					Authorization: store.getters.jwtToken
				}
			}
		);
		return response.data;
	},

	createTempOrder({productSelections, auxiliaryProductSelection, tempOrderOptions}) {
		tempOrderOptions.languageCode = store.getters.lastLocale;
		return BifrostBackendApi.post(`/temp-orders`,
			{
				tenantId: tenantConfig.tenantId,
				productSelections,
				auxiliaryProductSelection,
				tempOrderOptions
			},
			{
				headers: {
					Authorizationidp: store.getters.userIdpToken
				}
			});
	},

	findTempOrder() {
		return BifrostBackendApi.get(`/temp-orders`,
			{
				headers: {
					Authorizationidp: store.getters.userIdpToken
				},
				params: {
					tenantId: tenantConfig.tenantId
				}
			});
	},

	findForeignOrderNumber({ queryData }) {
		const { orderNumber, ...params } = queryData;
		return BifrostBackendNestApi.get(`/orders/${orderNumber}/_foreignOrderNumber`, {
			params
		});
	},
	getOrderDetailsByOrderNumber({ queryData }) {
		const { orderNumber, ...params } = queryData;
		return BifrostBackendNestApi.get(`/orders/${orderNumber}/_details`, {
			params
		}).then(response => {
			return response.data;
		});
	}
};

export default Object.freeze(OrdersProvider);
